import classNames from "classnames";
import { useContext, useState } from "react";
import { JsonViewer } from "@textea/json-viewer";
import { UserContext } from "../../../contexts";
import {
	getTiersWithoutPrices,
	getRawProductWithoutPrices,
} from "../../../../../../common/premium_tiers";
import { DiscordEntitlementEntity } from "../../../../../../src/entity/DiscordEntitlement";
import { StripeEntitlementEntity } from "../../../../../../src/entity/StripeEntitlement";
import { trpc } from "../../../utils/trpc";
import { DashboardUtils } from "../../../dashboard_utils";
import { LoadingThrobber } from "../base_components/loading_throbber";
import { SeshPrimaryButton } from "../base_components/sesh_primary_button";
import { SeshWarningButton } from "../base_components/sesh_warning_button";
import { SeshTextInput } from "../base_components/sesh_text_input";
import { WithDropdown } from "../base_components/with_dropdown";
import { DiscordEntitlement } from "../premium_page/entitlements/discord_entitlement";
import { StripeEntitlementNoModifyButton } from "../premium_page/entitlements/stripe_entitlement";
import { InfoTooltip } from "../tooltips/info_tooltip";

// Simple confirmation modal for admin actions
function AdminConfirmModal(props: {
	open: boolean;
	onClose: () => void;
	onConfirm: () => void;
	title: string;
	message: string;
	confirmText?: string;
	isLoading?: boolean;
}) {
	if (!props.open) return null;

	return (
		<DashboardUtils.SeshModal
			is_active={props.open}
			onClose={props.onClose}
			title={props.title}
			content={
				<div className="flex flex-col gap-4">
					<p className="whitespace-pre-line text-slate-300">
						{props.message}
					</p>
					<div className="flex justify-end gap-2">
						<SeshPrimaryButton
							className="!w-auto bg-slate-600 px-4 hover:bg-slate-700"
							onClick={props.onClose}
						>
							Cancel
						</SeshPrimaryButton>
						<SeshWarningButton
							className="!w-auto px-4"
							onClick={props.onConfirm}
							is_loading={props.isLoading}
						>
							{props.confirmText ?? "Confirm"}
						</SeshWarningButton>
					</div>
				</div>
			}
		/>
	);
}

// Simple result modal to show success/error
function AdminResultModal(props: {
	open: boolean;
	onClose: () => void;
	title: string;
	message: string;
	isError?: boolean;
}) {
	if (!props.open) return null;

	return (
		<DashboardUtils.SeshModal
			is_active={props.open}
			onClose={props.onClose}
			title={props.title}
			content={
				<div className="flex flex-col gap-4">
					<div
						className={classNames(
							"flex items-start gap-3 rounded-lg p-4",
							props.isError
								? "border border-red-500/50 bg-red-500/10"
								: "border border-green-500/50 bg-green-500/10"
						)}
					>
						<i
							className={classNames(
								"fas mt-0.5",
								props.isError
									? "fa-exclamation-circle text-red-400"
									: "fa-check-circle text-green-400"
							)}
						></i>
						<p
							className={classNames(
								"whitespace-pre-line",
								props.isError ? "text-red-300" : "text-green-300"
							)}
						>
							{props.message}
						</p>
					</div>
					<div className="flex justify-end">
						<SeshPrimaryButton
							className="!w-auto px-4"
							onClick={props.onClose}
						>
							OK
						</SeshPrimaryButton>
					</div>
				</div>
			}
		/>
	);
}

const AdminViews = {
	Guild: {
		query: trpc.admin.getGuild.useQuery,
		element: (id: string) => <GuildView id={id} />,
	},
	User: {
		query: trpc.admin.getUser.useQuery,
		element: (id: string) => <UserView id={id} />,
	},
	Event: { query: trpc.admin.getEvent.useQuery, element: undefined },
	Poll: { query: trpc.admin.getPoll.useQuery, element: undefined },
	Sync: {
		query: trpc.admin.getSync.useQuery,
		element: undefined,
	},
} as const;

type QueryType = keyof typeof AdminViews;

export function AdminEntityViewer() {
	const user = useContext(UserContext);
	const [id_input, setIdInput] = useState<Record<QueryType, string>>({
		Event: "",
		Guild: "",
		Poll: "",
		User: "",
		Sync: "",
	});
	const [id, setId] = useState<Record<QueryType, string>>({
		Event: "",
		Guild: "",
		Poll: "",
		User: "",
		Sync: "",
	});
	const [tab, setTab] = useState<QueryType>("Guild");

	const handleFindMe = () => {
		if (user?.id) {
			setIdInput({
				...id_input,
				User: user.id,
			});
			setId({
				...id,
				User: user.id,
			});
			setTab("User");
		}
	};

	return (
		<div>
			<Tabs
				tab={tab}
				setTab={(tab) => {
					setTab(tab);
				}}
			/>
			<form
				className="w-full"
				onSubmit={(e) => {
					e.preventDefault();
					setId({
						...id,
						[tab]: id_input[tab],
					});
				}}
			>
				<label className="label">{tab} ID</label>
				<div className="flex gap-2">
					<SeshTextInput
						input_props={{
							value: id_input[tab],
							onChange: (e) =>
								setIdInput({
									...id_input,
									[tab]: e.currentTarget.value,
								}),
						}}
					/>
					<SeshPrimaryButton
						className="!w-fit"
						type="submit"
					>
						<i className="fas fa-search"></i>
					</SeshPrimaryButton>
					{tab === "User" && (
						<SeshPrimaryButton
							className="!w-fit whitespace-nowrap"
							type="button"
							onClick={handleFindMe}
						>
							<i className="fas fa-user mr-1"></i> Find Me
						</SeshPrimaryButton>
					)}
				</div>
			</form>
			{AdminViews[tab].element && AdminViews[tab].element(id[tab])}
			{id[tab] && <EntityViewer type={tab} id={id[tab]} />}
		</div>
	);
}

function Tabs(props: { tab: QueryType; setTab: (new_tab: QueryType) => void }) {
	const tabs: JSX.Element[] = [];

	for (const key in AdminViews) {
		tabs.push(
			<li
				key={key}
				className={classNames({
					"is-active bg-base-dark": props.tab === key,
				})}
				onClick={() => props.setTab(key as QueryType)}
			>
				<a className="manual-link !bg-inherit">{key}</a>
			</li>
		);
	}

	return (
		<div className="tabs is-boxed">
			<ul>{tabs}</ul>
		</div>
	);
}

function EntityViewer(props: { type: QueryType; id: string }) {
	const query = AdminViews[props.type].query({
		id: props.id,
	});

	if (!query.isSuccess) {
		return <LoadingThrobber />;
	}

	if (!query.data) {
		return <>Not found</>;
	}

	return <JsonViewer value={query.data} theme="dark" />;
}

function UserView(props: { id: string }) {
	return <UserEntitlementsTable id={props.id} />;
}

function GuildView(props: { id: string }) {
	const guild_query = trpc.admin.getGuild.useQuery({
		id: props.id,
	});
	const context = trpc.useContext();
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [confirmAction, setConfirmAction] = useState<"grant" | "revoke">("grant");

	const grantSeshProMutation = trpc.admin.grantGuildSeshPro.useMutation({
		onSuccess: () => {
			context.admin.getGuild.invalidate({ id: props.id });
			setShowConfirmModal(false);
		},
	});

	if (!guild_query.data) {
		return <></>;
	}

	const guild = guild_query.data;
	const hasSeshPro = Boolean(guild.sesh_pro_activator);

	const handleConfirm = () => {
		grantSeshProMutation.mutate({
			guild_id: props.id,
			grant: confirmAction === "grant",
		});
	};

	return (
		<div className="mb-8 flex flex-col gap-4">
			<AdminConfirmModal
				open={showConfirmModal}
				onClose={() => setShowConfirmModal(false)}
				onConfirm={handleConfirm}
				title={confirmAction === "grant" ? "Grant sesh Pro" : "Revoke sesh Pro"}
				message={
					confirmAction === "grant"
						? "Are you sure you want to grant sesh Pro to this guild?"
						: "Are you sure you want to revoke sesh Pro from this guild?"
				}
				confirmText={confirmAction === "grant" ? "Grant" : "Revoke"}
				isLoading={grantSeshProMutation.isLoading}
			/>

			{/* Grant/Revoke sesh Pro Section */}
			<div className="rounded-lg bg-base-dark p-4">
				<h3 className="mb-4 text-lg font-semibold">sesh Pro Status</h3>
				<div className="flex flex-wrap items-center gap-4">
					{hasSeshPro ? (
						<>
							<span className="flex items-center gap-2 text-green-400">
								<i className="fas fa-crown text-amber-300"></i>
								sesh Pro is <strong>ACTIVE</strong>
								{guild.sesh_pro_admin_granted && (
									<span className="rounded bg-amber-600 px-2 py-0.5 text-xs font-semibold text-white">
										ADMIN GRANTED
									</span>
								)}
								<span className="text-slate-400">
									(activated by {guild.sesh_pro_activator.id})
								</span>
							</span>
							<SeshPrimaryButton
								onClick={() => {
									setConfirmAction("revoke");
									setShowConfirmModal(true);
								}}
								is_loading={grantSeshProMutation.isLoading}
								className="!w-auto shrink-0 bg-red-600 px-3 hover:bg-red-700"
							>
								Revoke
							</SeshPrimaryButton>
						</>
					) : (
						<>
							<span className="text-slate-400">
								<i className="fas fa-crown mr-2 opacity-50"></i>
								sesh Pro is <strong>NOT ACTIVE</strong>
							</span>
							<SeshPrimaryButton
								onClick={() => {
									setConfirmAction("grant");
									setShowConfirmModal(true);
								}}
								is_loading={grantSeshProMutation.isLoading}
								className="!w-auto shrink-0 px-3"
							>
								Grant
							</SeshPrimaryButton>
						</>
					)}
				</div>
			</div>
		</div>
	);
}

function UserEntitlementsTable(props: { id: string }) {
	const context = trpc.useContext();
	const user_query = trpc.admin.getUser.useQuery({
		id: props.id,
	});

	// Modal states
	const [showClearConfirm, setShowClearConfirm] = useState(false);
	const [showSyncConfirm, setShowSyncConfirm] = useState(false);
	const [resultModal, setResultModal] = useState<{
		open: boolean;
		title: string;
		message: string;
		isError: boolean;
	}>({ open: false, title: "", message: "", isError: false });

	const clearStripeStatusMutation =
		trpc.admin.devClearUserStripeStatus.useMutation({
			onSuccess: (data) => {
				context.admin.getUser.invalidate({ id: props.id });
				setShowClearConfirm(false);
				setResultModal({
					open: true,
					title: "Stripe Status Cleared",
					message: `Deleted customer: ${data.deleted_stripe_customer_id ?? "none"}\nDeleted entitlements: ${data.deleted_entitlement_count}`,
					isError: false,
				});
			},
			onError: (error) => {
				setShowClearConfirm(false);
				setResultModal({
					open: true,
					title: "Error",
					message: error.message,
					isError: true,
				});
			},
		});

	const syncStripeEntitlementsMutation =
		trpc.admin.devSyncUserStripeEntitlements.useMutation({
			onSuccess: (data) => {
				context.admin.getUser.invalidate({ id: props.id });
				setShowSyncConfirm(false);
				setResultModal({
					open: true,
					title: "Entitlements Synced",
					message: `Old entitlements: ${data.old_entitlement_count}\nSubscriptions processed: ${data.subscriptions_processed}`,
					isError: false,
				});
			},
			onError: (error) => {
				setShowSyncConfirm(false);
				setResultModal({
					open: true,
					title: "Error",
					message: error.message,
					isError: true,
				});
			},
		});

	if (!props.id) {
		return <></>;
	}

	if (!user_query.data) {
		return <></>;
	}

	const hasStripeData =
		user_query.data.stripe_customer_id ||
		user_query.data.stripe_entitlements.length > 0;

	return (
		<div className="mb-24 flex flex-col gap-2">
			{/* Confirmation Modals */}
			<AdminConfirmModal
				open={showClearConfirm}
				onClose={() => setShowClearConfirm(false)}
				onConfirm={() => clearStripeStatusMutation.mutate({ user_id: props.id })}
				title="Clear All Stripe Data"
				message={`Are you sure you want to clear ALL Stripe data for user ${props.id}?\n\nThis will:\n- Delete them from Stripe\n- Remove all entitlements\n- Deactivate any Premium servers\n\nThis action cannot be undone!`}
				confirmText="Clear Stripe Status"
				isLoading={clearStripeStatusMutation.isLoading}
			/>

			<AdminConfirmModal
				open={showSyncConfirm}
				onClose={() => setShowSyncConfirm(false)}
				onConfirm={() => syncStripeEntitlementsMutation.mutate({ user_id: props.id })}
				title="Sync Entitlements from Stripe"
				message={`Sync entitlements from Stripe for user ${props.id}?\n\nThis will:\n- Delete existing subscription entitlements\n- Re-fetch subscriptions from Stripe\n- Recreate entitlements based on current subscription state`}
				confirmText="Sync from Stripe"
				isLoading={syncStripeEntitlementsMutation.isLoading}
			/>

			<AdminResultModal
				open={resultModal.open}
				onClose={() => setResultModal({ ...resultModal, open: false })}
				title={resultModal.title}
				message={resultModal.message}
				isError={resultModal.isError}
			/>

			{/* Dev-only: Clear Stripe Status Section */}
			<div className="rounded-lg border border-red-600 bg-red-950/30 p-4">
				<div className="mb-2 flex items-center gap-2">
					<span className="rounded bg-red-600 px-2 py-0.5 text-xs font-bold uppercase">
						Dev Only
					</span>
					<span className="font-semibold text-red-400">
						Clear All Stripe Data
					</span>
				</div>
				<p className="mb-3 text-sm text-slate-400">
					This will delete the user from Stripe (TEST mode only),
					remove all entitlements, and reset their subscription
					status. Only works in development.
				</p>
				<div className="flex items-center gap-2">
					<SeshPrimaryButton
						className="!w-auto bg-red-600 px-4 hover:bg-red-700"
						is_loading={clearStripeStatusMutation.isLoading}
						onClick={() => setShowClearConfirm(true)}
						disabled={!hasStripeData}
					>
						<i className="fas fa-trash mr-2"></i>
						Clear Stripe Status
					</SeshPrimaryButton>
					<SeshPrimaryButton
						className="!w-auto bg-blue-600 px-4 hover:bg-blue-700"
						is_loading={syncStripeEntitlementsMutation.isLoading}
						onClick={() => setShowSyncConfirm(true)}
						disabled={!user_query.data.stripe_customer_id}
					>
						<i className="fas fa-sync mr-2"></i>
						Sync from Stripe
					</SeshPrimaryButton>
					{!hasStripeData && (
						<span className="text-sm text-slate-500">
							No Stripe data to clear
						</span>
					)}
				</div>
			</div>

			<div className="text-xl font-bold">Stripe Entitlements</div>
			<div className="flex flex-col gap-2">
				{user_query.data.stripe_entitlements.map((val, index) => (
					<StripeAdminEntitlement
						key={val.id}
						entity={val}
						user_id={props.id}
						customer_id={user_query.data.stripe_customer_id}
					/>
				))}
			</div>
			<div className="w-fit">
				<AddDropdownButton user_id={props.id} />
			</div>
			<div className="text-xl font-bold">Discord Entitlements</div>
			<div className="flex flex-col gap-2">
				{user_query.data.discord_entitlements.map((val, index) => (
					<DiscordAdminEntitlement
						key={val.id}
						entity={val}
						user_id={props.id}
					/>
				))}
			</div>
		</div>
	);
}

function AddDropdownButton(props: { user_id: string }) {
	const tiers = getTiersWithoutPrices();

	const mutation = trpc.admin.addStripeEntitlement.useMutation({
		onSuccess: () =>
			context.admin.getUser.invalidate({
				id: props.user_id,
			}),
	});
	const context = trpc.useContext();

	const items: JSX.Element[] = [];
	if (!mutation.isLoading) {
		for (const product_id in tiers) {

			const product = getRawProductWithoutPrices(product_id);
			items.push(
				<div
					className="flex cursor-pointer gap-2 p-1 hover:bg-base-dark"
					onClick={() => {
						mutation.mutate({
							product_id: product_id,
							user_id: props.user_id,
						});
					}}
				>
					{product.title} ({product.activations})
				</div>
			);
		}
	} else {
		items.push(<>Loading...</>);
	}

	return (
		<WithDropdown items={items}>
			<div className="w-fit self-center font-bold">Add Entitlement</div>
		</WithDropdown>
	);
}

function StripeAdminEntitlement(props: {
	entity: Omit<StripeEntitlementEntity, "getNumberOfActivations">;
	user_id: string;
	customer_id: string;
}) {
	const context = trpc.useContext();

	const { entity, customer_id } = props;

	const delete_entitlement_mutation =
		trpc.admin.removeStripeEntitlement.useMutation({
			onSuccess: () => {
				context.admin.getUser.invalidate({
					id: props.user_id,
				});
			},
		});

	const [is_deleting_entitlement, setDeletingEntitlement] = useState(false);

	return (
		<div className="flex w-full items-center gap-2">
			<div className="w-full">
				<StripeEntitlementNoModifyButton
					activations={
						getRawProductWithoutPrices(entity.product_id)
							.activations
					}
					{...entity}
				/>
			</div>
			<SeshPrimaryButton
				className="!w-fit"
				is_loading={is_deleting_entitlement}
				onClick={() => {
					setDeletingEntitlement(true);
					delete_entitlement_mutation.mutate({
						entitlement_id: entity.id,
					});
				}}
			>
				Delete
			</SeshPrimaryButton>
			<InfoTooltip
				info={"Will only delete from sesh DB, not from Stripe's end"}
			></InfoTooltip>
		</div>
	);
}

function DiscordAdminEntitlement(props: {
	entity: DiscordEntitlementEntity;
	user_id: string;
}) {
	const context = trpc.useContext();

	const { entity } = props;

	const delete_entitlement_mutation =
		trpc.admin.removeDiscordEntitlement.useMutation({
			onSuccess: () => {
				context.admin.getUser.invalidate({
					id: props.user_id,
				});
			},
		});

	const [is_deleting_entitlement, setDeletingEntitlement] = useState(false);

	return (
		<div className="flex w-full items-center gap-2">
			<div className="w-full">
				<DiscordEntitlement {...entity} />
			</div>
			<SeshPrimaryButton
				className="flex w-fit items-center gap-2"
				is_loading={is_deleting_entitlement}
				onClick={() => {
					setDeletingEntitlement(true);
					delete_entitlement_mutation.mutate({
						entitlement_id: entity.id,
					});
				}}
			>
				Delete{" "}
			</SeshPrimaryButton>
			<InfoTooltip
				info={"Will only delete from sesh DB, not from Stripe's end"}
			></InfoTooltip>
		</div>
	);
}
